import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, TrendingUp, Zap, AlertTriangle, LogOut, ChevronLeft, ChevronRight, Radio } from 'lucide-react';
import Dashboard from './components/Dashboard/Dashboard';
import ProductionDashboard from './components/Production/ProductionDashboard';
import EnergyDashboard from './components/Energy/EnergyDashboard';
import AlarmCenter from './components/Alarms/AlarmCenter';
import LoginPage from './components/Auth/LoginPage';
import PendingApproval from './components/Auth/PendingApproval';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AuthContext, { AuthProvider, useAuth } from './context/AuthContext';
import { isDemoMode } from './services/dataService';

const skipAuth = import.meta.env.VITE_SKIP_AUTH === 'true' || isDemoMode;

// Mock auth provider for skipAuth mode — gives DashboardLayout a valid auth context
// without needing Firebase Auth
const MockAuthProvider = ({ children }) => (
  <AuthContext.Provider value={{
    user: { email: 'demo@factoryos.io' },
    isApproved: true,
    loading: false,
    error: null,
    login: async () => {},
    signup: async () => {},
    logout: async () => {},
    resetPassword: async () => {},
    refreshApprovalStatus: async () => true,
    clearError: () => {},
  }}>
    {children}
  </AuthContext.Provider>
);

const getShiftInfo = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 14) return { name: 'Day Shift', time: '06:00 \u2013 14:00', color: 'text-amber-400' };
  if (hour >= 14 && hour < 22) return { name: 'Evening Shift', time: '14:00 \u2013 22:00', color: 'text-blue-400' };
  return { name: 'Night Shift', time: '22:00 \u2013 06:00', color: 'text-purple-400' };
};

// DashboardLayout always uses useAuth + useNavigate — no conditional hooks
const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const shift = getShiftInfo();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = async () => {
    if (!skipAuth) {
      await logout();
      navigate('/login');
    }
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/production', icon: TrendingUp, label: 'Production' },
    { path: '/energy', icon: Zap, label: 'Energy & Maintenance' },
    { path: '/alarms', icon: AlertTriangle, label: 'Alarms' },
  ];

  return (
    <div className="flex h-screen bg-surface-900 overflow-hidden">
      <aside className={`${sidebarOpen ? 'w-60' : 'w-[68px]'} bg-surface-950 border-r border-surface-600/20 transition-all duration-300 ease-in-out flex flex-col flex-shrink-0`}>
        <div className="p-4 border-b border-surface-600/20">
          <div className="flex items-center gap-3">
            <img
              src="/emblem.svg"
              alt="BSR Robotics"
              className="w-9 h-9 flex-shrink-0"
            />
            {sidebarOpen && (
              <div className="overflow-hidden">
                <h1 className="text-sm font-bold text-slate-100 tracking-wide">FactoryOS</h1>
                <p className="text-[10px] text-slate-500 tracking-widest uppercase">Machine Monitor</p>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <NavLink key={item.path} to={item.path} end={item.path === '/'}
                className={({ isActive }) => isActive ? 'nav-item-active' : 'nav-item-inactive'}
                title={item.label}>
                <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {sidebarOpen && (
          <div className="px-4 py-3 border-t border-surface-600/20">
            <p className={`text-xs font-medium ${shift.color}`}>{shift.name}</p>
            <p className="text-[11px] text-slate-500">{shift.time}</p>
          </div>
        )}

        <div className="px-4 py-3 border-t border-surface-600/20">
          <div className={`flex items-center gap-2 ${sidebarOpen ? '' : 'justify-center'}`}>
            <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse-status" />
            {sidebarOpen && (
              <span className="text-[11px] text-slate-400">
                {isDemoMode ? 'Demo Mode' : 'Live \u2022 Firebase'}
              </span>
            )}
          </div>
        </div>

        {user && sidebarOpen && (
          <div className="px-4 py-3 border-t border-surface-600/20">
            <p className="text-[11px] text-slate-500 truncate mb-2">{user.email}</p>
            {!skipAuth && (
              <button onClick={handleLogout}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-300 transition-colors text-xs">
                <LogOut className="w-3.5 h-3.5" /><span>Sign out</span>
              </button>
            )}
          </div>
        )}

        <button onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-3 border-t border-surface-600/20 text-slate-500 hover:text-slate-300 transition-colors flex items-center justify-center">
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-surface-900/80 backdrop-blur-xl border-b border-surface-600/20 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-status" />
            <span className="text-xs text-slate-400">
              {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {' \u2022 '}
              <span className="font-mono text-slate-300">
                {currentTime.toLocaleTimeString('en-US', { hour12: false })}
              </span>
            </span>
          </div>
          <div className={`badge text-[10px] tracking-wider uppercase ${isDemoMode ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
            {isDemoMode ? 'Demo Mode' : 'Live'}
          </div>
        </div>
        <div className="p-6">{children}</div>
      </main>
    </div>
  );
};

const DashboardRoutes = () => (
  <DashboardLayout>
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/production" element={<ProductionDashboard />} />
      <Route path="/energy" element={<EnergyDashboard />} />
      <Route path="/alarms" element={<AlarmCenter />} />
    </Routes>
  </DashboardLayout>
);

const App = () => {
  // skipAuth: use MockAuthProvider (no Firebase Auth needed), skip login
  // normal:  use real AuthProvider with login/approval flow
  const Provider = skipAuth ? MockAuthProvider : AuthProvider;

  return (
    <Provider>
      <Router>
        <Routes>
          {!skipAuth && <Route path="/login" element={<LoginPage />} />}
          {!skipAuth && <Route path="/pending-approval" element={<PendingApproval />} />}
          <Route path="/*" element={
            skipAuth ? <DashboardRoutes /> : <ProtectedRoute><DashboardRoutes /></ProtectedRoute>
          } />
        </Routes>
      </Router>
    </Provider>
  );
};

export default App;
